import type { Renderable } from '@adornis/chemistry/renderable.js';
import { nothing } from 'lit';
import { until } from 'lit/directives/until.js';

export function ellipsis(text: string, length: number) {
  return text.length > length ? text.slice(0, length) + '...' : text;
}

let cachedResults: Record<string, { result: Renderable; timestamp: number }> = {};

function clearResults() {
  const now = Date.now();
  for (const key in cachedResults) {
    const elem = cachedResults[key];
    if (!elem || now > elem.timestamp) {
      delete cachedResults[key];
    }
  }
}

/**
 * Cached version of until directive. This can help in avoid multiple fetches of the promise if fast re-render is happening.
 * @param id Unique identifier for the cached result. Could be a _id or any unique string.
 * @param fn Function that returns a promise of the renderable.
 * @param loading Renderable to show while the promise is being resolved.
 * @param ttl Time to live for the cached result in milliseconds.
 * @returns Renderable
 */
export function cachedUntil(
  id: string,
  fn: () => Promise<Renderable>,
  loading: Renderable = nothing,
  ttl: number = 1000,
) {
  clearResults();

  const now = Date.now();
  const cached = cachedResults[id];

  if (cached) {
    return cached.result;
  }

  return until(
    (async () => {
      const res = await fn();
      cachedResults[id] = { result: res, timestamp: now + ttl };
      return res;
    })(),
    loading,
  );
}
